import cx from 'classnames';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Sticky from 'react-sticky-el';

import useIsMobile from '../../../hooks/use-is-mobile';
import useLinkProps from '../../../hooks/use-link-props';
import Breadcrumbs from '../../modules/v2/breadcrumbs';
import Help from '../../modules/help';
import SearchBadges from '../../modules/search-badges';
import Button from '../../ui/button';
import Chevrelon from '../../ui/icons/cheverlon';
import IconSearch from '../../ui/icons/search';
import PartsFilter from '../../ui/inputs/filters/parts';
import { useCurrentGroupInfo } from '../../ui/inputs/filters/parts/use-current-group-info';
import ReferenceFilter from '../../ui/inputs/filters/reference';
import FlowTitleSavedCars from '../../ui/v2/flow-title/saved-car-title';
import BPImage from '../../ui/v2/image';
import getCategoryBySlug from '../vehicle/utils/get-category-by-slug';

import ChoiceModal from './choice-modal';
import InvalidForm from './invalid-form';
import MobileFilters from './mobile-filters';
import WarningMorePartsWihoutCar from './more-parts-without-car';
import classes from './products.module.css';
import Results from './results';
import { useAskSearchChoice } from './search-choice';
import ProductsSchema from './seo';
import { useValidate } from './validation-catch';
import ZeroResults from './zero-results';
import {
  actualStepAtom,
  catalogueGroupsAtom,
  isCatalogueMenuOpenAtom,
  selectedCatalogueSelectionAtom,
} from '../../../state/selection';
import CatalogueWithSelectedPart from '../../ui/icons/catalogue-with-selected-part';
import CatalogueWithoutSelectedPart from '../../ui/icons/catalogue-without-selected-part';
import {
  allSavedCarsAtom,
  currentSavedCarAtom,
  selectVehicleStepAtom,
  unsavedInformationAtom,
  vehiclePickerOpen,
} from '../../../state/vehicle';
import {
  brandAtom,
  modelAtom,
  versionAtom,
} from '../../../hooks/use-picker-selection';
import useToken from '../../../hooks/use-token';
import { useAppContext } from '../../providers/app-context';
import EcoRepairScoreModule from '../../modules/eco-repair-score';
import { checkPartAvailabity } from '../../../utils/check-part-availabilty';
import useLoggedUser from '../../../hooks/use-logged-user';
import ProIcon from '../../ui/icons/pro-icon';
import FeaturedLink from '../../ui/links/featured';
import Link from 'next/link';
import ProBanner from '../../modules/pro-banner';
import saveUserCars from '../../../services/user/save-cars';
import Cookies from 'js-cookie';
import useSelectVehicle from '../../modules/vehicle-picker/use-selected-vehicle';

export type TProductSearchFormFields = {
  category: string;
  subCategory: string;
  priceFrom: string;
  priceTo: string;
  vehicle: string;
  reference: string;
};

const ProductsPage: FunctionComponent<TPageProducts> = ({
  groups,
  products,
  breadcrumbs,
  isReferencePage,
  isLoading,
  incompleteSearch,
  translatedSlugs,
  vehicleData,
  savedVehicles,
  refetchVehicles,
}) => {
  const { push, query, events: routerEvents, locale = 'en' } = useRouter();
  const csrftoken = useToken();
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const { tenantConfig, brand } = useAppContext();
  const methods = useForm({
    defaultValues: {
      category: query?.category ?? '',
      subCategory: query?.subCategory ?? '',
      priceFrom: query?.priceFrom ?? '',
      priceTo: query?.priceTo ?? '',
      vehicle: query?.vehicle ?? '',
      reference: query?.reference ?? '',
    },
  });
  const { handleSubmit, watch, setFocus } = methods;
  const partSearchRef = useRef<HTMLInputElement>(null);
  const referenceRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const searchChoice = useAskSearchChoice();
  const current = useCurrentGroupInfo({
    groups,
    currentPart: query?.subCategory as string,
  });
  const [vehiclePicker, setVehiclePicker] = useAtom(vehiclePickerOpen);
  const [, setBrandAtom] = useAtom(brandAtom);
  const [, setModelAtom] = useAtom(modelAtom);
  const [, setVersionAtom] = useAtom(versionAtom);
  const [, setCurrentSavedCar] = useAtom(currentSavedCarAtom);
  const [, setSelectedVehicleStep] = useAtom(selectVehicleStepAtom);
  const someOnlyProAvailable = groups.filter(c => c.someNotAllowed).length > 0;
  const categoryNotAllowed = query?.category ? groups.filter(c => query?.category === c.slug).length === 0 : false;
  const subCategoryAllowed = query?.subCategory && !categoryNotAllowed ? checkPartAvailabity(groups, query?.category, query?.subCategory) : false;
  const user = useLoggedUser();
  const isProUser = user?.staff?.is_pro;
  const proRegisterLink = getLink('pro');
  const setUserVehicle = useSelectVehicle();

  const [, setAllSavedCars] = useAtom(allSavedCarsAtom);

  useEffect(() => {
    setAllSavedCars(savedVehicles);
  }, [savedVehicles, setAllSavedCars]);

  useEffect(() => {
    if (routerEvents) {
      const handleRouteChanged = () => {
        methods.reset({
          category: query?.category ?? '',
          subCategory: query?.subCategory ?? '',
          priceFrom: query?.priceFrom ?? '',
          priceTo: query?.priceTo ?? '',
          vehicle: query?.vehicle ?? '',
          reference: query?.reference ?? '',
        });
      };
      routerEvents.on('routeChangeComplete', handleRouteChanged);
      refetchVehicles();
      return () => routerEvents.off('routeChangeComplete', handleRouteChanged);
    }
  }, [routerEvents, methods, query]);

  const onSubmit = useCallback(
    ({
      reference,
      category,
      subCategory,
      vehicle,
      priceFrom,
      priceTo,
    }: TProductSearchFormFields) => {
      const {
        reference: referenceQuery,
        category: categoryQuery,
        subCategory: subCategoryQuery,
        vehicle: vehicleQuery,
        priceFrom: priceFromQuery,
        priceTo: priceToQuery,
        page,
        ...restTokeep
      } = query;

      const link = getLink('search', {
        ...restTokeep,
        ...(reference && { reference }),
        ...(category && { category }),
        ...(subCategory && { subCategory }),
        ...(vehicle && { vehicle }),
        ...(priceFrom && { priceFrom }),
        ...(priceTo && { priceTo }),
      });

      push(link.href, link.as, { shallow: true, scroll: true });
    },
    [getLink, push, query]
  );

  useEffect(() => {
    if (methods.formState.isDirty) handleSubmit(onSubmit)();
  }, [methods, handleSubmit, onSubmit]);

  const value = watch('reference');

  const [refi, setValues] = useState(value || '');
  const [searchForReference, setSearchForReference] = useState<boolean>(false);
  const [, setOpen] = useAtom(isCatalogueMenuOpenAtom);
  const [selectedCatalogueSelection, setSelectedCatalogueSelection] = useAtom(
    selectedCatalogueSelectionAtom
  );
  const [, setActualStep] = useAtom(actualStepAtom);
  const [, setFilteredGroups] = useAtom(catalogueGroupsAtom);
  const [unsavedInformation, setUnsavedInformation] = useAtom(
    unsavedInformationAtom
  );

  const { brandAlias, modelImage } = useMemo(() => {
    if (vehicleData) {
      return {
        brandAlias: vehicleData?.info?.brand?.alias,
        modelImage: vehicleData?.info?.model?.image,
      };
    }
    return {
      brandAlias: undefined,
      modelImage: undefined,
    };
  }, [vehicleData]);

  const openCatalogueAvailable = groups.filter(
    (group) => group.total_products > 0
  );

  const openCatalogueSidebar = () => {
    setFilteredGroups(groups);
    setOpen(true);
    if (current) {
      setSelectedCatalogueSelection(current);
      setActualStep(1);
    } else {
      setSelectedCatalogueSelection(null);
      setActualStep(0);
    }
  };

  const openVehiclePicker = async () => {
    setBrandAtom(null);
    setModelAtom(null);
    setVersionAtom(null);
    setCurrentSavedCar(null);
    setUnsavedInformation(false);

    if (
      vehicleData?.info &&
      vehicleData?.info.brand &&
      !vehicleData?.info.model
    ) {
      setSelectedVehicleStep('SELECT_MODEL_UNSAVED');
      setUnsavedInformation(true);
      setBrandAtom(vehicleData?.info.brand);
      setVehiclePicker((prevValue) => !prevValue);
      return;
    }

    if (
      (vehicleData?.info && vehicleData?.info.model) ||
      (savedVehicles && savedVehicles?.length > 0)
    ) {
      const isActualCarSaved = savedVehicles?.filter(el => el.model?.slug === vehicleData?.info?.model?.slug || el.version?.slug === vehicleData?.info?.version?.slug)

      if (isActualCarSaved?.length === 0 && (vehicleData?.info?.version?.slug || vehicleData?.info?.model?.slug)) {
        setUserVehicle(vehicleData?.info?.version?.slug || vehicleData?.info?.model?.slug)
      }
      setSelectedVehicleStep('SELECT_SAVED_CAR');
      if (vehicleData?.info && vehicleData?.info.model) {
        setCurrentSavedCar(vehicleData?.info);
      }
    } else {
      if (tenantConfig.id === 8 && brand) {
        setSelectedVehicleStep('SELECT_MODEL');
      } else {
        setSelectedVehicleStep('SELECT_BRAND');
      }
    }
    setVehiclePicker((prevValue) => !prevValue);
    return;
  };

  return (
    <>
      {products && (
        <ProductsSchema
          breadcrumbs={breadcrumbs}
          products={products}
          translatedSlugs={translatedSlugs}
        />
      )}
      <Breadcrumbs
        items={breadcrumbs}
        currentPage={
          (products?.search.reference as string) ||
          getCategoryBySlug(
            {
              category: query?.category as string,
              subCategory: query?.subCategory as string,
            },
            groups
          )?.subCategory ||
          t('vehicle-picker:search')
        }
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={cx('partsContent hybridSavedCarTitle lg:grid', 'm-auto')}
          >
            <div className="">
              <div className="bg-neutral-5 shadow">
                <FlowTitleSavedCars
                  vehicleData={vehicleData}
                  brandAlias={brandAlias}
                  modelImage={modelImage}
                  savedVehicles={savedVehicles}
                ></FlowTitleSavedCars>
              </div>
              {isMobile && (
                <>
                  <div className="grid items-end w-full bg-white px-4 py-4 mt-1">
                    <ReferenceFilter ref={referenceRef} />
                  </div>
                  <div
                    className={cx(
                      {
                        'pl-4 pb-4':
                          query?.reference ||
                          (query?.category && query?.subCategory) ||
                          query?.priceFrom ||
                          query?.priceTo,
                      },
                      {
                        hidden: !(
                          query?.reference ||
                          (query?.category && query?.subCategory) ||
                          query?.priceFrom ||
                          query?.priceTo
                        ),
                      }
                    )}
                  >
                    <SearchBadges
                      priceTemplate={
                        products?.price_range_list.length
                          ? products?.price_range_list[0]?.label
                          : undefined
                      }
                    />
                  </div>
                  <MobileFilters
                    onClose={handleSubmit(onSubmit)}
                    isReferencePage={isReferencePage}
                    filters={{
                      part: (
                        <PartsFilter
                          ref={partSearchRef}
                          groups={groups}
                          currentPart={query?.subCategory as string}
                        />
                      ),
                    }}
                    selectedPart={current}
                    groups={groups}
                  />
                  <Sticky
                    mode="top"
                    boundaryElement=".hybridSavedCarTitle"
                    hideOnBoundaryHit={false}
                    wrapperClassName="hidden"
                    stickyClassName={cx('!block bg-white !z-[12] shadow-md')}
                  >
                    <div
                      className={cx('transform transition-transform', {
                        'translate-x-0': !searchForReference,
                        '-translate-x-full': searchForReference,
                      })}
                    >
                      <div
                        className={cx('flex flex-nowrap whitespace-nowrap', {
                          'overflow-x-hidden': !searchForReference,
                        })}
                      >
                        <div className={cx('lg:h-full min-w-full')}>
                          <div className="flex items-center justify-end w-full">
                            <Button
                              isFullWidth={false}
                              onClick={(e) => {
                                e.preventDefault();
                                //setSearchForReference(!searchForReference);
                                if (refi !== value) {
                                  setValues(value);
                                }
                                window.scrollTo(0, 0);
                                referenceRef.current?.focus();
                              }}
                            >
                              <IconSearch className="text-neutral-80 w-4 h-4" />
                            </Button>

                            <>
                              <div
                                className={cx(
                                  {
                                    'justify-between': !vehicleData,
                                  },
                                  classes.stickybar
                                )}
                              >
                                <Button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openVehiclePicker();
                                  }}
                                  className="flex flex-row justify-between gap-4 items-center border-x border-x-brand-10 px-2"
                                >
                                  <>
                                    <span className="max-w-[90%] truncate">
                                      {vehicleData
                                        ? `${vehicleData?.info?.brand?.name || ''
                                        } ${vehicleData?.info?.model?.name || ''
                                        } ${vehicleData?.info?.version?.name ||
                                        ''
                                        }`
                                        : savedVehicles
                                          ? t(
                                            savedVehicles.length === 1
                                              ? 'common:saved_cars.one'
                                              : 'common:saved_cars.more',
                                            {
                                              count: savedVehicles.length,
                                            }
                                          )
                                          : t('common:saved_cars.add-a-car')}
                                    </span>
                                    {brandAlias ? (
                                      <BPImage
                                        src={`${process.env.STATIC_PREFIX}/brands/logos/${brandAlias}.svg`}
                                        fallbackSrc={`/svg/icons/car.svg`}
                                        alt={vehicleData?.info.brand.name}
                                        key={vehicleData?.info.brand.name}
                                        width={24}
                                        height={24}
                                      ></BPImage>
                                    ) : (
                                      <BPImage
                                        src={`/svg/icons/car.svg`}
                                        alt={'no car selected'}
                                        key={vehicleData?.info.brand.name || ''}
                                        width={16}
                                        height={16}
                                      ></BPImage>
                                    )}
                                  </>
                                </Button>
                              </div>
                              <Button
                                onClick={() => {
                                  if (openCatalogueAvailable.length === 0) {
                                    return;
                                  }
                                  openCatalogueSidebar();
                                }}
                                isFullWidth={false}
                                className={cx(
                                  'bg-white',
                                  'items-center flex shrink !w-[48px] !px-0 '
                                )}
                                disabled={openCatalogueAvailable.length === 0}
                                type="button"
                              >
                                <div className="w-full flex items-center justify-center">
                                  {current ? (
                                    <>
                                      <CatalogueWithSelectedPart
                                        className={cx('h-4', 'w-4', {
                                          'text-neutral-50':
                                            openCatalogueAvailable.length === 0,
                                        })}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <CatalogueWithoutSelectedPart
                                        className={cx('h-4', 'w-4', {
                                          'text-neutral-50':
                                            openCatalogueAvailable.length === 0,
                                        })}
                                      />
                                    </>
                                  )}
                                </div>
                              </Button>
                            </>
                          </div>
                        </div>
                        <div
                          className={cx('min-w-full', {
                            hidden: !searchForReference,
                          })}
                        >
                          <div className="flex items-center w-full">
                            <Button
                              isFullWidth={false}
                              onClick={(e) => {
                                e.preventDefault();
                                setSearchForReference(!searchForReference);
                                if (refi !== value) {
                                  setValues(value);
                                }
                              }}
                              className=""
                            >
                              <Chevrelon className="text-neutral-80 rotate-90 w-4 h-4"></Chevrelon>
                            </Button>

                            <ReferenceFilter
                              ref={referenceRef}
                              border={false}
                              sideBorder={true}
                              mainProps="flex flex-row w-full justify-between"
                            ></ReferenceFilter>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Sticky>
                </>
              )}
            </div>
            <div
              className={cx(
                classes.main,
                'container',
                'lg:grid',
                'm-auto gap-4 hybridDesktopReference'
              )}
            >
              <div className="hidden lg:flex flex-col gap-4 h-[92vh]">
                <Sticky
                  mode="top"
                  boundaryElement=".hybridSavedCarTitle"
                  hideOnBoundaryHit={false}
                  wrapperClassName={cx('flex flex-col gap-4 flex h-[80vh]')}
                  stickyClassName={cx(
                    'z-10 max-lg:pb-4 !h-[100vh]',
                    products?.results_total > 0 ? 'lg:pb-[95px]' : ' lg:pb-4'
                  )}
                >
                  <PartsFilter
                    ref={partSearchRef}
                    groups={groups}
                    currentPart={query?.subCategory as string}
                  />
                  {!isProUser && <ProBanner />}
                  <div className="pr-4 mt-4">
                    <Help />
                  </div>

                  <div className='max-lg:hidden'>
                    <EcoRepairScoreModule isPLP></EcoRepairScoreModule>
                  </div>
                </Sticky>
              </div>
              <div className={cx('')}>
                {!isMobile && <ReferenceFilter ref={referenceRef} />}
                {!isMobile && (
                  <div className={cx('pb-4')}>
                    <SearchBadges
                      priceTemplate={
                        products?.price_range_list.length
                          ? products?.price_range_list[0]?.label
                          : undefined
                      }
                    />
                  </div>
                )}
                <output className="relative block">
                  <ChoiceModal
                    zeroResults={products?.results_total === 0}
                    groups={groups}
                    searchChoice={searchChoice}
                    products={products}
                    extraContent={
                      products?.others_with_reference &&
                      products.others_with_reference >
                      products.results_total && (
                        <WarningMorePartsWihoutCar
                          others_with_reference={products.others_with_reference}
                          reference={products.search.reference}
                        />
                      )
                    }
                    zeroResultsContent={
                      products?.results_total === 0 && (
                        <ZeroResults
                          search={products.search}
                          others_with_reference={products.others_with_reference}
                          totalResults={products.results_total}
                          vehicleData={vehicleData}
                        />
                      )
                    }
                  />
                  {products?.results_total > 0 && (
                    <>
                      {incompleteSearch && incompleteSearch.length > 0 && (
                        <InvalidForm
                          errors={incompleteSearch}
                          partSearchRef={partSearchRef}
                          savedVehicles={savedVehicles}
                          vehicleData={vehicleData}
                          openCatalogueSidebar={openCatalogueSidebar}
                          referenceRef={referenceRef}
                        />
                      )}
                      <Results
                        isLoading={isLoading}
                        search={products.search}
                        others_with_reference={products.others_with_reference}
                        results={products.results}
                        totalResults={products.results_total}
                        currentPage={products.page_current}
                        lastPage={products.page_total}
                        isReferencePage={isReferencePage}
                        someOnlyProAvailable={someOnlyProAvailable}
                        groups={groups}
                      />
                    </>
                  )}
                </output>
                <div className='lg:hidden max-lg:px-4 max-lg:pb-6 flex gap-4 flex-col'>
                  {!isProUser && <ProBanner />}
                  <EcoRepairScoreModule isPLP></EcoRepairScoreModule>
                </div>
              </div>
            </div>
          </div>
        </form >
      </FormProvider >
    </>
  );
};

const ProductPageWithBoundary: FunctionComponent<TPageProducts> = (props) => {
  const { query } = useRouter();
  const incompleteSearch = useValidate(query);

  return <ProductsPage {...props} incompleteSearch={incompleteSearch} />;
};

export default ProductPageWithBoundary;
